import {QueryClient, useMutation, useQueryClient} from "@tanstack/react-query";
import axios, {AxiosError, AxiosResponse, CancelToken} from "axios";
import {message} from "antd";
import {useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";
import {useClientV2} from "../../../../utils/hooks";
import {API_BASE_URL_V2} from "../../../../services/config";
import {
  GET_STORE_SUBSCRIPTION_RQ_KEY,
  SUBSCRIPTION_COVER_IMAGE_DELETE_MUTATION_KEY,
  SUBSCRIPTION_COVER_IMAGE_UPLOAD_MUTATION_KEY,
} from "../constants";
import {CoverImagesItemResponse} from "../../../products/types";
import type {GetSubscriptionApiResponse} from "../get-subscription/types";

/** v2 POST /uploads/cover-image expects `cover-image` parts; the UI builds `file` + metadata. */
function toV2CoverImageFormData(source: FormData): FormData {
  const next = new FormData();
  Array.from(source.entries()).forEach(([key, value]) => {
    if (key === "file") {
      if (value instanceof Blob) {
        next.append("cover-image", value);
      }
    } else if (key === "offeringId" || key === "offeringType") {
      next.append(key, String(value));
    }
  });
  return next;
}

/** Envelope from POST /uploads/cover-image (e.g. status, message, data.mediaUrls). */
type V2CoverImageUploadEnvelope = {
  data?: {
    mediaUrls?: string[];
    media_files?: CoverImagesItemResponse[];
  };
};

/** Map v2 `{ data: { mediaUrls } }` to the legacy `{ data: { media_files } }` shape callers expect. */
function adaptV2CoverUploadAxiosResponse(
  raw: AxiosResponse<unknown>,
  offeringId: number | string
): AxiosResponse<{data: {media_files: CoverImagesItemResponse[]}}> {
  const body = raw.data as V2CoverImageUploadEnvelope;
  const inner = body?.data;
  const offeringIdNum = Number(offeringId);

  let media_files: CoverImagesItemResponse[];

  if (inner?.media_files && Array.isArray(inner.media_files)) {
    media_files = inner.media_files;
  } else if (inner?.mediaUrls && Array.isArray(inner.mediaUrls)) {
    media_files = inner.mediaUrls.map((url, index) => ({
      id: `pending-refetch-${Date.now()}-${index}`,
      media_url: url,
      offering_id: offeringIdNum,
      createdAt: "",
      updatedAt: "",
      media_type: "image" as const,
      offering_type: "product" as const,
      user_id: 0,
    }));
  } else {
    media_files = [];
  }

  return {
    ...raw,
    data: {
      data: {media_files},
    },
  };
}

function appendUploadedCoverImagesToSubscriptionCache(
  queryClient: QueryClient,
  subscriptionId: string | number,
  newFiles: CoverImagesItemResponse[]
) {
  if (newFiles.length === 0) return;

  const queryKey = [GET_STORE_SUBSCRIPTION_RQ_KEY, String(subscriptionId)] as const;
  const idNum = Number(subscriptionId);

  queryClient.setQueryData<GetSubscriptionApiResponse>(queryKey, (previous) => {
    const existing = previous?.productSubscription?.cover_images ?? [];
    const seenUrls = new Set(existing.map((c) => c.media_url));
    const toAdd = newFiles.filter((c) => c.media_url && !seenUrls.has(c.media_url));
    if (toAdd.length === 0) return previous;

    if (!previous?.productSubscription) {
      return {
        productSubscription: {
          id: idNum,
          name: "",
          slug: "",
          description: "",
          cover_images: toAdd,
          createdAt: "",
          updatedAt: "",
          bookingQuestions: [],
          tiers: [],
          subscribers: [],
        },
      };
    }

    return {
      ...previous,
      productSubscription: {
        ...previous.productSubscription,
        cover_images: [...existing, ...toAdd],
      },
    };
  });
}

export const useSubscriptionUploadCoverImages = () => {
  const queryClient = useQueryClient();
  const {userInfo} = useSelector((state: RootState) => state.userLogin);

  return useMutation<
    AxiosResponse<{data: {media_files: CoverImagesItemResponse[]}}, any>,
    AxiosError<{message: string}, any>,
    {
      data: FormData;
      offeringId: number | string;
      onProgress?: (progress: number) => void;
      cancelToken?: CancelToken;
    },
    unknown
  >({
    mutationKey: [SUBSCRIPTION_COVER_IMAGE_UPLOAD_MUTATION_KEY],
    mutationFn: async (payload: {
      data: FormData;
      offeringId: number | string;
      onProgress?: (progress: number) => void;
      cancelToken?: CancelToken;
    }) => {
      const v2FormData = toV2CoverImageFormData(payload.data);
      const url = `${API_BASE_URL_V2}uploads/cover-image`;
      const raw = await axios.post<unknown>(url, v2FormData, {
        headers: {
          ...(userInfo?.accessToken && {
            Authorization: `Bearer ${userInfo.accessToken}`,
          }),
        },
        onUploadProgress: (event) => {
          if (event.total) {
            const percent = Math.round((event.loaded * 100) / event.total);
            payload.onProgress?.(percent);
          }
        },
        cancelToken: payload.cancelToken,
      });
      return adaptV2CoverUploadAxiosResponse(raw, payload.offeringId);
    },
    onSuccess: (response, variables) => {
      const newFiles = response.data?.data?.media_files ?? [];
      appendUploadedCoverImagesToSubscriptionCache(
        queryClient,
        variables.offeringId,
        newFiles
      );
    },
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
    },
    onSettled: (d, e, values) =>
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SUBSCRIPTION_RQ_KEY, String(values.offeringId)],
      }),
  });
};

export const useSubscriptionCoverImagesDelete = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();

  return useMutation({
    mutationKey: [SUBSCRIPTION_COVER_IMAGE_DELETE_MUTATION_KEY],
    mutationFn: async (payload: {mediaId: string; storeServiceId: string | number}) => {
      return client(`uploads/media/${payload.mediaId}`, {
        method: "DELETE",
      });
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSettled: (data, error, variables) =>
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SUBSCRIPTION_RQ_KEY, String(variables.storeServiceId)],
      }),
  });
};
